import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";
import type { z } from "zod";

import { EVENT_PREFIX } from "@/features/analytics/models";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackBmiPageViewed } from "@/features/analytics/trackers/bmi-page-viewed";
import {
  imperialBmiInputsSchema,
  MAX_HEIGHT_FEET,
  MAX_HEIGHT_INCHES,
  weightSchema,
} from "@/shared/models";
import {
  Form,
  Input,
  RulerIcon,
  SectionHeader,
  Select,
  WeigherIcon,
} from "@/shared/ui";
import { QuestionNavigation, QuestionStep } from "../components";
import { QUESTIONNAIRE_ROUTE } from "../router/constants";
import { useQuestionnaireStore } from "../stores";

const measurementsSchema = imperialBmiInputsSchema
  .pick({ heightFeet: true, heightInches: true })
  .extend({
    initialWeight: weightSchema,
    expectedWeight: weightSchema,
  })
  .refine(
    (data) =>
      !Number.isFinite(data.initialWeight) ||
      !Number.isFinite(data.expectedWeight) ||
      data.expectedWeight < data.initialWeight,
    {
      error: () => t`Goal weight must be lower than your current weight`,
      path: ["expectedWeight"],
    },
  );

type MeasurementsValues = z.infer<typeof measurementsSchema>;

const FEET_OPTIONS = Array.from({ length: MAX_HEIGHT_FEET - 3 }, (_, i) => ({
  value: i + 4,
  label: `${i + 4} ft`,
}));

const INCHES_OPTIONS = Array.from(
  { length: MAX_HEIGHT_INCHES + 1 },
  (_, i) => ({ value: i, label: `${i} in` }),
);

export const Measurements = () => {
  const navigate = useNavigate();
  const { analytics } = useCustomerIoContext();
  const heightFeet = useQuestionnaireStore((store) => store.heightFeet);
  const heightInches = useQuestionnaireStore((store) => store.heightInches);
  const initialWeight = useQuestionnaireStore((store) => store.initialWeight);
  const expectedWeight = useQuestionnaireStore((store) => store.expectedWeight);
  const setHeight = useQuestionnaireStore((store) => store.setHeight);
  const setInitialWeight = useQuestionnaireStore(
    (store) => store.setInitialWeight,
  );
  const setExpectedWeight = useQuestionnaireStore(
    (store) => store.setExpectedWeight,
  );

  const form = useForm<MeasurementsValues>({
    resolver: zodResolver(measurementsSchema),
    mode: "onTouched",
    defaultValues: { heightFeet, heightInches, initialWeight, expectedWeight },
  });

  useEffect(() => {
    trackBmiPageViewed({
      customerIoAnalytics: analytics,
      eventPrefix: EVENT_PREFIX.QUESTIONNAIRE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = form.handleSubmit((values) => {
    setHeight({
      heightFeet: values.heightFeet,
      heightInches: values.heightInches,
    });
    setInitialWeight(values.initialWeight);
    setExpectedWeight(values.expectedWeight);
    navigate(QUESTIONNAIRE_ROUTE.PERSONAL_INFO);
  });

  return (
    <Form.Provider {...form}>
      <QuestionStep onSubmit={handleNext} footer={<QuestionNavigation />}>
        <SectionHeader
          title={t`Let's start with your measurements`}
          description={t`This helps us find the right treatment for you.`}
        />

        <div className="flex w-full flex-col gap-6 sm:max-w-xl">
          <div className="grid gap-4 sm:grid-cols-2">
            <Form.Field
              control={form.control}
              name="heightFeet"
              render={({ field: { value, ...field } }) => (
                <Form.Item className="mf-masked mf-listen">
                  <Form.Label aria-required>{t`Height (feet)`}</Form.Label>
                  <Form.Control>
                    <Select
                      value={value ?? ""}
                      options={FEET_OPTIONS}
                      placeholder={t`Select feet`}
                      leftIcon={<RulerIcon />}
                      contentClassName="mf-masked mf-listen"
                      {...field}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="heightInches"
              render={({ field: { value, ...field } }) => (
                <Form.Item className="mf-masked mf-listen">
                  <Form.Label aria-required>{t`Height (inches)`}</Form.Label>
                  <Form.Control>
                    <Select
                      value={value ?? ""}
                      options={INCHES_OPTIONS}
                      placeholder={t`Select inches`}
                      leftIcon={<RulerIcon />}
                      contentClassName="mf-masked mf-listen"
                      {...field}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />
          </div>

          <Form.Field
            control={form.control}
            name="initialWeight"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label
                  aria-required
                >{t`Current weight (pounds)`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    type="number"
                    placeholder={t`E.g. 286`}
                    {...form.register("initialWeight", { valueAsNumber: true })}
                  >
                    <Input.Slot>
                      <WeigherIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <Form.Field
            control={form.control}
            name="expectedWeight"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`Goal weight (pounds)`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    type="number"
                    placeholder={t`E.g. 220`}
                    {...form.register("expectedWeight", {
                      valueAsNumber: true,
                    })}
                  >
                    <Input.Slot>
                      <WeigherIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />
        </div>
      </QuestionStep>
    </Form.Provider>
  );
};
